import { useQuery } from '@tanstack/react-query';
import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import { useAuthStore } from '@/stores/authStore';
import { useOrganization } from './useOrganizations';
import { asApiError } from '@/utils/apiErrorMessage';
import { mapWithConcurrency, NOTE_FETCH_CONCURRENCY } from '@/utils/mapWithConcurrency';
import type { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';

type EmptyObject = Record<string, never>;

/**
 * Hook for fetching notes belonging to an organization.
 * Uses the dedicated organization notes endpoint (bypasses normal note access checks).
 */
export function useOrganizationNotes(organizationId: string | undefined, enabled = true) {
  const userId = useAuthStore((state) => state.userData?.id);
  const { data: organization, isLoading: isLoadingOrg, error: orgError } = useOrganization(organizationId, enabled);

  const noteIds = organization?.note_ids ?? [];

  const {
    data: notes,
    isLoading: isLoadingNotes,
    error,
    refetch,
  } = useQuery({
    queryKey: ['organization-notes', userId, organizationId, noteIds],
    queryFn: async () => {
      if (noteIds.length === 0 || !organizationId) {
        return [];
      }

      // Count of notes that failed for a reason that is NOT "gone" or "not yours".
      let hardFailures = 0;

      // Fetch notes through the organization-specific endpoint, bounded so a
      // large team can't open one XHR per note simultaneously.
      const results = await mapWithConcurrency(noteIds, NOTE_FETCH_CONCURRENCY, async (noteId) => {
        try {
          const note = await noteGoApiFetch<
            NoteOut,
            unknown,
            undefined,
            EmptyObject,
            EmptyObject,
            { organization_id: string; note_id: string }
          >({
            url: '/organizations/{organization_id}/notes/{note_id}',
            method: 'GET',
            pathParams: { organization_id: organizationId, note_id: noteId },
          });
          return note;
        } catch (err: unknown) {
          const status = asApiError(err).status;

          // Per-note outcomes that are EXPECTED and must not fail the whole view:
          //  - 404: the note was deleted.
          //  - 403: the note lives in a folder this user can't access. The
          //    "General" bucket is computed as note_ids minus notes in *visible*
          //    folders, so restricted ids do reach this fetch by design — the
          //    403 is what actually keeps them off screen. Never escalate these,
          //    or a single restricted note blanks the whole team page.
          if (status !== 404 && status !== 403) {
            // Real failure (401 expired token, 5xx, network). Record it, but keep
            // going: escalating a single bad note would discard every note that
            // DID load and produce exactly the false empty state we're avoiding.
            hardFailures += 1;
          }
          return null;
        }
      });

      // Filter out nulls and inactive notes
      const loaded = results.filter((note): note is NoteOut => note !== null && note.active !== false);

      // Nothing loaded AND something genuinely failed (expired token mid-session,
      // backend down) — that is data loss, not an empty team. Throw so React Query
      // reports `error` and the page shows a failure instead of the misleading
      // "Notes you share with this team will appear here" empty state.
      if (loaded.length === 0 && hardFailures > 0) {
        throw new Error(`Failed to load this team's notes (${hardFailures} of ${noteIds.length} requests failed).`);
      }

      return loaded;
    },
    enabled: enabled && !!organizationId && !isLoadingOrg && !!userId,
    staleTime: 30000,
  });

  return {
    organization,
    notes: notes ?? [],
    isLoading: isLoadingOrg || isLoadingNotes,
    isLoadingOrg,
    error,
    orgError,
    refetch,
  };
}
